import { useState } from "react";
import { Button } from "@/components/ui/button";
import { JobStatusBadge } from "@/components/job-status-badge";
import type { JobEvent } from "@/lib/jobs";

// ----------------------------------------------------------------------------
// Time-ago helper (no external dep)
// ----------------------------------------------------------------------------

function timeAgo(isoUtc: string): string {
  const diffMs = Date.now() - new Date(isoUtc).getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ----------------------------------------------------------------------------
// Filter options
// ----------------------------------------------------------------------------

type FilterKind = "All" | "StatusChanged" | "Edited" | "InvoiceGenerated";

const FILTER_LABELS: Record<FilterKind, string> = {
  All: "All",
  StatusChanged: "Status changed",
  Edited: "Edited",
  InvoiceGenerated: "Invoice generated",
};

function matchesFilter(event: JobEvent, filter: FilterKind): boolean {
  if (filter === "All") return true;
  return event.eventKind === filter;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export interface JobEventListProps {
  events: JobEvent[];
}

export function JobEventList({ events }: JobEventListProps) {
  const [filter, setFilter] = useState<FilterKind>("All");

  const visible = events.filter((e) => matchesFilter(e, filter));

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div role="group" aria-label="Event filter" className="flex flex-wrap gap-1">
        {(Object.keys(FILTER_LABELS) as FilterKind[]).map((kind) => (
          <Button
            key={kind}
            type="button"
            size="sm"
            variant={filter === kind ? "default" : "outline"}
            onClick={() => setFilter(kind)}
          >
            {FILTER_LABELS[kind]}
          </Button>
        ))}
      </div>

      {/* Empty state */}
      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status" aria-label="No events">
          No events to display.
        </p>
      )}

      {/* Timeline */}
      <ol className="space-y-2">
        {visible.map((event) => (
          <li
            key={event.jobEventId}
            className="flex flex-col gap-0.5 rounded-md border p-3 text-sm"
            data-event-kind={event.eventKind}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{event.actorUserName}</span>
              <time
                dateTime={event.occurredOnUtc}
                className="text-xs text-muted-foreground"
                title={new Date(event.occurredOnUtc).toLocaleString("en-AU")}
              >
                {timeAgo(event.occurredOnUtc)}
              </time>
            </div>

            <span className="text-xs text-muted-foreground">{event.eventKind}</span>

            {(event.fromStatusCode || event.toStatusCode) && (
              <div className="flex items-center gap-1.5 mt-1">
                {event.fromStatusCode && (
                  <JobStatusBadge code={event.fromStatusCode} />
                )}
                {event.fromStatusCode && event.toStatusCode && (
                  <span className="text-xs text-muted-foreground" aria-hidden>→</span>
                )}
                {event.toStatusCode && (
                  <JobStatusBadge code={event.toStatusCode} />
                )}
              </div>
            )}

            {event.comment && (
              <p className="mt-1 text-xs text-muted-foreground">{event.comment}</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
